import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ValidationError } from 'yup';
import {
  Caption1,
  Card,
  Divider,
  Field,
  Input,
  Subtitle2,
  Textarea,
  Title3,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { DocumentBulletListRegular } from '@fluentui/react-icons';
import config from '@config/index';
import ExpandableDescription from '@shared/components/ExpandableDescription';
import { useUpdateChangeTemplateDescription, useUpdateChangeTemplateTitle } from '../api';
import { CHANGE_TEMPLATE_DESCRIPTION_MAX_LENGTH } from '../constants';
import type { TChangeTemplateDetails, TChangeTemplateOutletContext } from '../types';
import { changeTemplateTitleSchema } from '../components/changeTemplateForm';

const useStyles = makeStyles({
  icon: {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    fontSize: '28px',
  },
});

const ChangeTemplateIcon = () => {
  const classes = useStyles();
  return (
    <div className={classes.icon}>
      <DocumentBulletListRegular />
    </div>
  );
};

type TFieldsProps = Pick<TChangeTemplateDetails, 'changeTemplate'> & { description: string };

// Mounted only in edit mode, so each visit starts from the stored values.
const ChangeTemplateGeneralFields = ({ changeTemplate, description: savedDescription }: TFieldsProps) => {
  const updateTitle = useUpdateChangeTemplateTitle(changeTemplate._id);
  const updateDescription = useUpdateChangeTemplateDescription(changeTemplate._id);
  const [title, setTitle] = useState(changeTemplate.title);
  const [titleError, setTitleError] = useState('');
  const [description, setDescription] = useState(savedDescription);
  // A save shows the stored value, e.g. the title without surrounding spaces.
  useEffect(() => setTitle(changeTemplate.title), [changeTemplate.title]);
  useEffect(() => setDescription(savedDescription), [savedDescription]);

  const saveTitle = () => {
    let next: string;
    try {
      next = changeTemplateTitleSchema.validateSync(title);
    } catch (error) {
      if (!(error instanceof ValidationError)) throw error;
      setTitleError(error.message);
      return;
    }
    setTitleError('');
    if (next !== changeTemplate.title) updateTitle.mutate(next);
    else setTitle(next);
  };

  return (
    <>
      <div className="flex items-start gap-4">
        <ChangeTemplateIcon />
        {/* Centers the large input on the icon, top-aligned so a validation message below moves neither. */}
        <Field
          validationState={titleError ? 'error' : 'none'}
          validationMessage={titleError}
          className="mt-1 min-w-0 flex-1"
        >
          <Input
            size="large"
            value={title}
            maxLength={config.ITEM_NAME_MAX_LENGTH}
            placeholder="Title"
            input={{ className: '!text-2xl !font-semibold' }}
            onChange={(_, data) => setTitle(data.value)}
            onBlur={saveTitle}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.currentTarget.blur();
            }}
          />
        </Field>
      </div>
      <Divider />
      <div className="flex flex-col gap-2">
        <Subtitle2>Description</Subtitle2>
        <Textarea
          value={description}
          rows={6}
          resize="vertical"
          maxLength={CHANGE_TEMPLATE_DESCRIPTION_MAX_LENGTH}
          placeholder="Add a description"
          onChange={(_, data) => setDescription(data.value)}
          onBlur={() => {
            if (description !== savedDescription) updateDescription.mutate(description || null);
          }}
        />
        <Caption1 className="text-neutralFg-3">Changes save when you leave a field.</Caption1>
      </div>
    </>
  );
};

const ChangeTemplateGeneral = () => {
  const {
    details: { changeTemplate },
    selected,
    canChange,
  } = useOutletContext<TChangeTemplateOutletContext>();
  return (
    <Card size="large" className="!gap-5 !p-6">
      {canChange ? (
        <ChangeTemplateGeneralFields changeTemplate={changeTemplate} description={selected.description ?? ''} />
      ) : (
        <>
          <div className="flex items-center gap-4">
            <ChangeTemplateIcon />
            <Title3 className="min-w-0 break-words">{changeTemplate.title}</Title3>
          </div>
          <Divider />
          <div className="flex flex-col gap-2">
            <Subtitle2>Description</Subtitle2>
            <ExpandableDescription description={selected.description} />
          </div>
        </>
      )}
    </Card>
  );
};

export default ChangeTemplateGeneral;
